import React, { useRef } from 'react'
import styled from 'styled-components'
import UploadCloudIcon from '../../icons/IconLib/UploadCloudIcon'
import FolderIcon from '../../icons/IconLib/FolderIcon'
import CameraIcon from '../../icons/IconLib/CameraIcon'
import { getFileTypeColor } from './fileUpload.constants'
import { DocGlyph, SheetGlyph } from './fileUpload.glyphs'

export interface FileDropzoneProps {
  accept: string[]
  maxSizeMb: number
  isDragActive: boolean
  hasError?: boolean
  /** Extension of the currently selected file — highlights its format chip. */
  activeExtension?: string | null
  hint?: string
  /**
   * Добавляет вторую, «камерную» кнопку со своим input (`capture`).
   * По умолчанию выключено — файловый флоу (импорт таблиц) её не получает.
   */
  allowCamera?: boolean
  onFilePick: (file: File | null) => void
  dragHandlers: {
    onDragOver: (e: React.DragEvent<HTMLElement>) => void
    onDragLeave: (e: React.DragEvent<HTMLElement>) => void
    onDrop: (e: React.DragEvent<HTMLElement>) => void
  }
}

const normalizeExtension = (entry: string): string =>
  entry.startsWith('.') ? entry.toLowerCase() : `.${entry.toLowerCase()}`

const SPREADSHEET_EXTENSIONS = ['.xls', '.xlsx', '.csv']

const FormatGlyph = ({ extension }: { extension: string }) =>
  SPREADSHEET_EXTENSIONS.includes(extension) ? (
    <SheetGlyph strokeWidth={2.6} />
  ) : (
    <DocGlyph strokeWidth={2.6} />
  )

/**
 * Drag-n-drop zone with a native file dialog fallback and dynamic format chips.
 * Purely presentational — validation and upload live in `useFileUpload`.
 */
const FileDropzone = ({
  accept,
  maxSizeMb,
  isDragActive,
  hasError,
  activeExtension,
  hint,
  allowCamera = false,
  onFilePick,
  dragHandlers,
}: FileDropzoneProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const extensions = accept.map(normalizeExtension)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilePick(e.target.files?.[0] ?? null)
    // Same file can be re-picked after an error.
    e.target.value = ''
  }

  return (
    <Zone $over={isDragActive} $error={!!hasError} {...dragHandlers}>
      <ZoneIcon $over={isDragActive} $error={!!hasError}>
        <UploadCloudIcon size={26} />
      </ZoneIcon>
      <ZoneTitle>{isDragActive ? 'שחררו כדי להעלות' : 'גררו קובץ לכאן'}</ZoneTitle>
      <ZoneHint>{hint ?? `מקסימום ${maxSizeMb} MB, קובץ אחד בכל פעם`}</ZoneHint>
      <OrDivider>או</OrDivider>
      <PickButton type='button' onClick={() => inputRef.current?.click()}>
        <FolderIcon size={15} />
        בחירת קובץ
      </PickButton>
      <HiddenInput
        ref={inputRef}
        type='file'
        accept={extensions.join(',')}
        onChange={handleInputChange}
      />
      {allowCamera && (
        <>
          <CameraButton
            type='button'
            onClick={() => cameraInputRef.current?.click()}
            aria-label='צילום'
            title='צילום'
          >
            <CameraIcon size={17} />
          </CameraButton>
          {/*
            Отдельный вход именно под съёмку: `capture` открывает камеру напрямую,
            поэтому вешать его на основной input нельзя — там он убрал бы выбор
            «снять или выбрать». Android без него камеру в системном шите не
            предлагает, т.к. основной accept состоит только из расширений.
          */}
          <HiddenInput
            ref={cameraInputRef}
            type='file'
            accept='image/*'
            capture='environment'
            onChange={handleInputChange}
          />
        </>
      )}
      <FormatBar>
        {extensions.map((ext) => (
          <FormatChip key={ext} $match={activeExtension === ext}>
            <FormatSwatch $color={getFileTypeColor(ext)}>
              <FormatGlyph extension={ext} />
            </FormatSwatch>
            {ext.slice(1).toUpperCase()}
          </FormatChip>
        ))}
      </FormatBar>
      <FormatNote>
        {`ניתן להעלות: ${extensions.join(', ')} · עד ${maxSizeMb} MB`}
      </FormatNote>
    </Zone>
  )
}

export default FileDropzone

const Zone = styled.div<{ $over: boolean; $error: boolean }>`
  direction: rtl;
  border: 1.5px dashed ${({ theme }) => theme.colors.inputBorder};
  border-radius: ${({ theme }) => theme.radii.md};
  background: ${({ theme }) => theme.colors.bgLayout};
  padding: 2.125rem 1.5rem 1.875rem;
  text-align: center;
  transition:
    border-color ${({ theme }) => theme.motion.base} ease,
    background ${({ theme }) => theme.motion.base} ease,
    box-shadow ${({ theme }) => theme.motion.base} ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.inputBorderHover};
  }

  ${({ $over, theme }) =>
    $over &&
    `
    border-color: ${theme.colors.accentDeep};
    border-style: solid;
    background: ${theme.colors.accentSoft};
    box-shadow: ${theme.shadows.focus};
  `}

  ${({ $error, theme }) =>
    $error &&
    `
    border-color: ${theme.colors.danger};
    background: ${theme.colors.dangerSoft};
  `}
`

const ZoneIcon = styled.div<{ $over: boolean; $error: boolean }>`
  width: 3.5rem;
  height: 3.5rem;
  margin: 0 auto 1rem;
  border-radius: ${({ theme }) => theme.radii.md};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.line};
  display: grid;
  place-items: center;
  color: ${({ theme }) => theme.colors.icon};
  transition:
    transform ${({ theme }) => theme.motion.base} ease,
    color ${({ theme }) => theme.motion.base} ease,
    border-color ${({ theme }) => theme.motion.base} ease;

  ${({ $over, theme }) =>
    $over &&
    `
    transform: translateY(-4px) scale(1.04);
    color: ${theme.colors.accentDeep};
    border-color: ${theme.colors.accentDeep};
  `}

  ${({ $error, theme }) =>
    $error &&
    `
    color: ${theme.colors.danger};
    border-color: ${theme.colors.danger};
  `}
`

const ZoneTitle = styled.h4`
  margin: 0 0 0.3125rem;
  font-size: ${({ theme }) => theme.text.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.ink};
`

const ZoneHint = styled.div`
  font-size: ${({ theme }) => theme.text.md};
  color: ${({ theme }) => theme.colors.ink3};
  margin-bottom: 1.125rem;
`

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  max-width: 13.75rem;
  margin: 0 auto 1rem;
  color: ${({ theme }) => theme.colors.ink4};
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.xs};

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) => theme.colors.line};
  }
`

const PickButton = styled.button`
  height: 2.375rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 1rem;
  border-radius: ${({ theme }) => theme.radii.md};
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.md};
  font-weight: 500;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.inputBorder};
  color: ${({ theme }) => theme.colors.ink2};
  transition:
    background ${({ theme }) => theme.motion.fast} ease,
    border-color ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.inputBorderHover};
    background: ${({ theme }) => theme.colors.bgLayout};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }
`

/** Квадратная иконочная версия PickButton — только съёмка. */
const CameraButton = styled(PickButton)`
  width: 2.375rem;
  padding: 0;
  justify-content: center;
  margin-inline-start: 0.5rem;

  /* На десктопе capture игнорируется, и кнопка просто дублировала бы «בחירת קובץ». */
  @media (pointer: fine) {
    display: none;
  }
`

const HiddenInput = styled.input`
  display: none;
`

const FormatBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4375rem;
  justify-content: center;
  margin-top: 1rem;
`

const FormatChip = styled.span<{ $match: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 1.625rem;
  padding-inline: 0.4375rem 0.5625rem;
  border-radius: ${({ theme }) => theme.radii.pill};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.line};
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.xs};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.ink2};
  transition:
    border-color ${({ theme }) => theme.motion.fast} ease,
    background ${({ theme }) => theme.motion.fast} ease;

  ${({ $match, theme }) =>
    $match &&
    `
    border-color: ${theme.colors.accentDeep};
    background: ${theme.colors.accentSoft};
  `}
`

const FormatSwatch = styled.span<{ $color: string }>`
  width: 0.875rem;
  height: 0.875rem;
  border-radius: 0.25rem;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
  display: grid;
  place-items: center;
  color: ${({ theme }) => theme.colors.surface};

  svg {
    width: 0.5625rem;
    height: 0.5625rem;
  }
`

const FormatNote = styled.div`
  margin-top: 0.625rem;
  text-align: center;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.xs};
  color: ${({ theme }) => theme.colors.ink4};
`
